import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHead from "@/components/base/SEOHead";
import { getArticleById, getRelatedArticles } from "@/mocks/articleData";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleById(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.relatedArticles);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "author": {
      "@type": "Organization",
      "name": article.author,
    },
    "datePublished": article.date,
    "image": article.image,
    "publisher": {
      "@type": "Organization",
      "name": "爱威奶",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.aiweinai.com/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "产品分类",
        "item": "https://www.aiweinai.com/category/all",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.category,
        "item": `https://www.aiweinai.com/category/${article.category === "儿童系列" ? "children" : article.category === "成人系列" ? "adult" : article.category === "中老年系列" ? "senior" : article.category === "运动营养" ? "sport" : article.category === "使用指南" ? "guide" : article.category === "用户评测" ? "review" : "all"}`,
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": article.title,
        "item": `https://www.aiweinai.com/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 爱威奶`}
        description={article.content[0]?.type === "p" ? article.content[0].text!.slice(0, 150) : article.title}
        keywords={`爱威奶,${article.category},${article.title}`}
        canonicalUrl={`https://www.aiweinai.com/article/${article.id}`}
        ogType="article"
        ogTitle={article.title}
        ogDescription={article.content[0]?.type === "p" ? article.content[0].text!.slice(0, 150) : article.title}
        ogUrl={`https://www.aiweinai.com/article/${article.id}`}
        schemaJson={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 py-8 md:py-10">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category/all" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">产品分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">{article.category}</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 line-clamp-1">{article.title}</span>
            </nav>
            <div className="flex items-center gap-2 text-xs text-foreground-400 mb-3">
              <span className="bg-accent-50 text-accent-700 px-2 py-0.5 rounded-full">{article.category}</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-snug">
              {article.title}
            </h1>
          </div>
        </div>

        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <article className="prose-custom">
            {article.content.map((block, index) => {
              if (block.type === "p") {
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    {block.text}
                  </p>
                );
              }
              if (block.type === "h2") {
                return (
                  <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "h3") {
                return (
                  <h3 key={index} className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-8 mb-3">
                    {block.text}
                  </h3>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote key={index} className="border-l-3 border-primary-400 pl-5 py-2 my-6 bg-primary-50/50 rounded-r-lg">
                    <p className="text-sm md:text-base text-foreground-600 italic leading-relaxed">
                      {block.text}
                    </p>
                  </blockquote>
                );
              }
              return null;
            })}
          </article>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra: NonNullable<ReturnType<typeof getArticleById>>) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-lg p-4 hover:bg-background-200/50 transition-colors duration-200 cursor-pointer"
                  >
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                      {ra.title}
                    </p>
                    <p className="text-xs text-foreground-500">{ra.date} · {ra.readTime}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}